import {
  FunctionComponent,
  useMemo,
  useCallback,
  useEffect,
  useState,
} from "react";
import { useAuth } from "../../context/AuthContext";
import { Controller, useForm } from "react-hook-form";
import DateSelector from "../../components/common/DateSelector";
import { useLazyQuery, useQuery } from "@apollo/client";
import {
  GET_CALENDAR_MEETINGS,
  MEETING_TYPES_CALENDAR,
  EMPLOYEE_TASKS_LIST_CALENDAR,
  PROJECT_GOALS_SHORT,
  PODS_DATA,
} from "../../api/query";
import {
  addDays,
  endOfMonth,
  format,
  startOfMonth,
} from "date-fns";
import Loader from "../../components/common/Loader";
import backArrow from "../../assets/task/back_arrow.svg";
import { useNavigate } from "react-router-dom";
import SearchableInputsDropdown from "../../components/common/SearchableDropdown";
import CalendarAdmin from "./CalendarAdmin";
import MemberListPopup from "./MemberListPopup";


const statusOptions = [
  { key: "All", name: "All" },
  { key: "Not Started", name: "Not Started" },
  { key: "In Progress", name: "In Progress" },
  { key: "Testing", name: "Testing" },
  { key: "Closed", name: "Closed" },
];

const ViewCalendarAdmin: FunctionComponent = () => {
  const {
    userInfo,
    setStartDateCalendar,
    setStartDateCalendarDay,
    setEndDateCalendar,
    startDateCalendar,
    setCalendarDate,
    currentViewAdmin,
    calendarDate,
    setCurrentViewAdmin,
    endDateCalendar,
    calendarFilter,
    setCalendarFilter,
    taskFilter,
    setTaskFilter,
    goalFilter,
    setGoalFilter,
    selectedEmployee,
    setSelectedEmployee,
  } = useAuth();

  const agencyId = userInfo?.agencies?.id;
  const navigate = useNavigate();
  const [selectedPod, setSelectedPod] = useState("0");
  const [selectedPodName, setSelectedPodName] = useState("All");
  const [clearEmployeeDrop, setClearEmployeeDrop] = useState(false)

  const [memberPopup, setMemberPopup] = useState<{
    open: boolean;
    title: string;
    members: any[];
    columns: {
      key: string;
      label: string;
    }[];
    onViewItem?: (item: any) => void;
  }>({
    open: false,
    title: "",
    members: [],
    columns: [],
  });
  const [employeeOptions, setEmployeeOptions] = useState<any[]>([
    { key: "All", name: "All Employees" },
  ]);
  const employeeId =
    selectedEmployee && selectedEmployee !== "All"
      ? Number(selectedEmployee)
      : null;
  const { control } = useForm<any>();

  // ==================== MEETINGS QUERIES ====================
  const { data: meetingTypesData, loading: meetingTypesDataLoading } = useQuery(
    MEETING_TYPES_CALENDAR,
    { fetchPolicy: "no-cache" },
  );

  const [getMeetings, { data: meetingsResponse, loading: meetingsLoading }] =
    useLazyQuery(GET_CALENDAR_MEETINGS, { fetchPolicy: "no-cache" });

  // ==================== TASKS QUERIES ====================
  const [
    getEmployeeTaskList,
    { data: employeeTaskListData, loading: tasksLoading },
  ] = useLazyQuery(EMPLOYEE_TASKS_LIST_CALENDAR, { fetchPolicy: "no-cache" });

  // ==================== GOALS QUERIES  ====================
  const [getEmployeeGoalList, { data: goalsData, loading: goalsLoading }] =
    useLazyQuery(PROJECT_GOALS_SHORT, { fetchPolicy: "no-cache" });

  // ==================== POD QUERIES  ====================
  const [getPOD, { data: podData, loading: podLoading }] = useLazyQuery(
    PODS_DATA,
    { fetchPolicy: "no-cache" },
  );

  // ==================== POD DATA ====================
  const podDataFormatted = useMemo(() => {
    return podData?.pods_data?.data || [];
  }, [podData]);

  useEffect(() => {
    const pod = podDataFormatted.find(
      (item: any) => String(item.pod_id) === String(selectedPod),
    );

    const members = [...(pod?.members || [])].sort((a: any, b: any) =>
      `${a.first_name} ${a.last_name}`.localeCompare(
        `${b.first_name} ${b.last_name}`,
        undefined,
        { sensitivity: "base" }
      )
    );

    setEmployeeOptions([
      { key: "All", name: "All Employees" },
      ...members.map((emp: any) => ({
        key: String(emp.employee_id),
        name: `${emp.first_name} ${emp.last_name}`,
      })),
    ]);
  }, [selectedPod, podDataFormatted]);

  const selectedEmployees = useMemo(() => {
    return (
      podDataFormatted.find(
        (x: any) => String(x.pod_id) === String(selectedPod),
      )?.members || []
    );
  }, [selectedPod, podDataFormatted]);

  // Keep all POD Summary employee lists alphabetically sorted
  // by first name + last name.
  const sortedSelectedEmployees = useMemo(() => {
    return [...selectedEmployees].sort((a: any, b: any) =>
      `${a.first_name || ""} ${a.last_name || ""}`.localeCompare(
        `${b.first_name || ""} ${b.last_name || ""}`,
        undefined,
        { sensitivity: "base" }
      )
    );
  }, [selectedEmployees]);

  const resources = useMemo(() => {
    const employees =
      selectedEmployee === "All"
        ? selectedEmployees
        : selectedEmployees.filter(
          (emp: any) => String(emp.employee_id) === String(selectedEmployee),
        );
    return employees.map((emp: any) => ({
      id: String(emp.employee_id),
      title: `${emp.first_name} ${emp.last_name}`,
      total_working_hours: emp.total_working_hours,
      allocated_working_hours: emp.allocated_working_hours,
      utilization_percentage: emp.utilization_percentage,
    }));
  }, [selectedEmployees]);

  // ==================== MEETING TYPE OPTIONS ====================
  const meetingTypeOptions = useMemo(() => {
    const meetingTypes = meetingTypesData?.meeting_types?.meeting_types || [];
    return [
      { key: "0", name: "All" },
      ...meetingTypes.map((mt: any) => ({
        key: mt.id,
        name: mt.name,
      })),
    ];
  }, [meetingTypesData]);

  const selectedMeetingName = useMemo(() => {
    if (calendarFilter === 0) return "All";
    const selected = meetingTypeOptions.find(
      (option) => option.key === String(calendarFilter),
    );
    return selected?.name || "All";
  }, [calendarFilter, meetingTypeOptions]);

  // ==================== MEETINGS DATA ====================
  const meetingData = useMemo(() => {
    return (
      meetingsResponse?.get_meetings?.meetings?.filter(
        (meeting: any) => meeting.is_deleted === false,
      ) || []
    );
  }, [meetingsResponse]);

const meetingEvents = useMemo(() => {
  return meetingData.flatMap((meeting: any) => {
    const members = meeting.members || [];

    // If a specific employee is selected, create event only for that employee
    if (selectedEmployee && selectedEmployee !== "All") {
      const member = members.find(
        (m: any) => String(m.employee_id) === String(selectedEmployee)
      );

      if (!member) return [];

      return [
        {
          id: `${meeting.id}-${member.employee_id}`,
          meeting_id:meeting.id,
          title: meeting.title,
          start: meeting.start_datetime,
          end: meeting.end_datetime,
          start_time:format(meeting.start_datetime, "hh:mm a"),
          end_time:format(meeting.end_datetime, "hh:mm a"),
          resourceId: String(member.employee_id),
          meeting_type_name:meeting.meeting_type_name,
          host_name:meeting.host_name,
          is_email_send:meeting.is_email_send,
          eventType: "meeting",
        },
      ];
    }

    // "All" selected -> create event for every meeting member
    return members.map((member: any) => ({
      id: `${meeting.id}-${member.employee_id}`,
      meeting_id:meeting.id,
      title: meeting.title,
      start: meeting.start_datetime,
      end: meeting.end_datetime,
      start_time:format(meeting.start_datetime, "hh:mm a"),
      end_time:format(meeting.end_datetime, "hh:mm a"),
      resourceId: String(member.employee_id),
      meeting_type_name:meeting.meeting_type_name,
      host_name:meeting.host_name,
      eventType: "meeting",
    }));
  });
}, [meetingData, selectedEmployee]);


  // ==================== MEETINGS FOR POPUP ====================
  const meetingsForPopup = useMemo(() => {
    return meetingData.map((meeting: any) => ({
      title: meeting.title,
      dateTime: `${format(new Date(meeting.start_datetime), "MM/dd/yyyy hh:mm a")} - ${format(new Date(meeting.end_datetime), "hh:mm a")}`,
      host_name: meeting.host_name || "-",
      attendees:
        meeting.members
          ?.map((m: any) => employeeOptions.find((it:any) => it.key == m.employee_id)?.name)
          .join(", ") || "-",
      meeting_type_name: meeting.meeting_type_name || "-",
      _meetingId: meeting.id,
      _startMeetDate: format(new Date(meeting.start_datetime), "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'"),
      _endMeetDate: format(new Date(meeting.end_datetime), "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'"),
    }));
  }, [meetingData,employeeOptions]);


  // ==================== TASKS DATA ====================
  const tasksData = useMemo(() => {
    return (
      employeeTaskListData?.employee_tasks_list?.tasks?.filter(
        (task: any) => task.start_datetime && task.end_datetime,
      )?.map((t:any)=>{
        return{
          ...t,
                 current_status: t.current_status_name
        }
      }) || []
    );
  }, [employeeTaskListData]);

  const taskEvents = useMemo(() => {
    return tasksData.map((task: any) => ({
      id: `${task.id}`,
      title: task.title,
      start: task.start_datetime,
      end: task.end_datetime,
      resourceId: String(task.assigned_to_id),
      goal_name:task.goal_name,
      project_name:task.project_name,
      hours:task.hours,
      mins:task.mins,
      current_status_name:(task.static_task_type_name === "Production Release" || task.static_task_type_name == "Merge and deploy") ?  task.static_task_type_name : task.current_status_name,
      eventType: "task",
    }));
  }, [tasksData]);

  // ==================== GOALS DATA ====================
  const goalsDataFormatted = useMemo(() => {
    const groupedGoals = goalsData?.project_goals_short?.grouped_goals || [];
    const formattedGoals: any[] = [];

    groupedGoals.forEach((group: any) => {
      group.goals?.forEach((goal: any) => {
        // Convert goal dates to datetime format if needed
        const startDateTime = goal.start_date
          ? new Date(goal.start_date).toISOString()
          : null;
        const endDateTime = goal.end_date
          ? new Date(goal.end_date).toISOString()
          : null;

        // Only include goals with date ranges
        if (startDateTime && endDateTime) {
          formattedGoals.push({
            id: goal.id,
            title: goal.title,
            start_datetime: startDateTime,
            end_datetime: endDateTime,
            created_by: goal.created_by,
            actual_start_date: goal.actual_start_date,
            actual_end_date: goal.actual_end_date,
            goal_static_outcome: goal.goal_static_outcome,
            goal_members: goal.goal_members,
            status_name: group.status_name, // Include the status from grouped_goals
            status_id: group.status_id,
            project_name: goal.project_name,
            event_type: "goal", // Add event type identifier
          });
        }
      });
    });

    return formattedGoals;
  }, [goalsData]);

const goalEvents = useMemo(() => {
  const events: any[] = [];

  goalsDataFormatted.forEach((goal: any) => {
    if (!goal.goal_members?.length) return;

    if (selectedEmployee && selectedEmployee !== "All") {
      const member = goal.goal_members.find(
        (m: any) => String(m.employee_id) === String(selectedEmployee)
      );

      if (!member) return;

      events.push({
        id: `${goal.id}-${member.employee_id}`,
        title: goal.title,
        start: goal.start_datetime,
        end: goal.end_datetime,
        start_date:format(new Date(goal.start_datetime), "MM/dd/yyyy"),
        end_date:format(new Date(goal.end_datetime), "MM/dd/yyyy"),
        resourceId: String(member.employee_id),
        goal_members:goal?.goal_members,
        project_name:goal?.project_name,
        status_name:goal?.status_name,
        eventType: "goal",
        
      });
    } else {
      goal.goal_members.forEach((member: any) => {
        events.push({
          id: `${goal.id}-${member.employee_id}`,
          title: goal.title,
          start: goal.start_datetime,
          end: goal.end_datetime,
          start_date:format(new Date(goal.start_datetime), "MM/dd/yyyy"),
          end_date:format(new Date(goal.end_datetime), "MM/dd/yyyy"),
          resourceId: String(member.employee_id),
          goal_members:goal?.goal_members,
          project_name:goal?.project_name,
          status_name:goal?.status_name,
          eventType: "goal",
        });
      });
    }
  });

  return events;
}, [goalsDataFormatted, selectedEmployee]);

  // ==================== STATUS COUNT OPTIONS ====================
  // Counts reflect the active filter: since the API already filters by status,
  // the data length is always the accurate count for the selected option.
  const taskFilterInitialValue = useMemo(
    () => `${taskFilter} (${tasksData.length})`,
    [taskFilter, tasksData.length],
  );

  const goalFilterInitialValue = useMemo(
    () => `${goalFilter} (${goalsDataFormatted.length})`,
    [goalFilter, goalsDataFormatted.length],
  );

  // ==================== COMBINED EVENTS ====================
  // Meeting filter has exclusive priority: when a specific meeting type is
  // selected (calendarFilter !== 0), hide all goal and task events.
  const resourceEvents: any = useMemo(() => {
    if (calendarFilter !== 0) {
      return [...meetingEvents];
    }
    return [...meetingEvents, ...taskEvents, ...goalEvents];
  }, [meetingEvents, taskEvents, goalEvents, calendarFilter]);

  // ==================== FETCH LOGIC ====================
  const fetchMeetings = useCallback(() => {
    if (!agencyId || calendarFilter === undefined || calendarFilter === null) {
      return;
    }

    getMeetings({
      variables: {
        start_date: format(startDateCalendar, "yyyy-MM-dd'T'00:00:00'Z'"),
        end_date: format(endDateCalendar, "yyyy-MM-dd'T'23:59:59'Z'"),
        agency_id: Number(agencyId),
        employee_id: employeeId,
        meeting_type_ids: calendarFilter === 0 ? null : [calendarFilter],
      },
    });
  }, [
    agencyId,
    selectedEmployee,
    calendarFilter,
    getMeetings,
    startDateCalendar,
    endDateCalendar,
  ]);

  const fetchTasks = useCallback(() => {
    if (!selectedEmployee) {
      return;
    }

    getEmployeeTaskList({
      variables: {
        employee_id: employeeId,
        start_date: format(startDateCalendar, "yyyy-MM-dd'T'00:00:00'Z'"),
        end_date: format(endDateCalendar, "yyyy-MM-dd'T'23:59:59'Z'"),
        page: 1,
        page_size: 400,
        task_status: taskFilter === "All" ? null : taskFilter,
      },
    });
  }, [
    selectedEmployee,
    getEmployeeTaskList,
    startDateCalendar,
    endDateCalendar,
    taskFilter,
  ]);

  const fetchGoals = useCallback(() => {
    if (!selectedEmployee) {
      return;
    }
    getEmployeeGoalList({
      variables: {
        employee_id: employeeId,
        start_date: format(startDateCalendar, "yyyy-MM-dd"),
        end_date: format(endDateCalendar, "yyyy-MM-dd"),
        is_archived: false,
        goal_status: goalFilter === "All" ? null : goalFilter,
      },
    });
  }, [
    selectedEmployee,
    getEmployeeGoalList,
    startDateCalendar,
    endDateCalendar,
    goalFilter,
  ]);

  const fetchPod = useCallback(() => {
    if (!selectedEmployee) {
      return;
    }

    getPOD({
      variables: {
        start_date: format(startDateCalendar, "yyyy-MM-dd"),
        end_date: format(endDateCalendar, "yyyy-MM-dd"),
        agency_id: parseInt(agencyId),
      },
    });
  }, [selectedEmployee, getPOD, startDateCalendar, endDateCalendar]);


  // Fetch meetings, goals , tasks when dependencies change
  useEffect(() => {
    fetchMeetings();
  }, [fetchMeetings]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  useEffect(() => {
    fetchGoals();
  }, [fetchGoals]);

  useEffect(() => {
    fetchPod();
  }, [fetchPod]);



  // ==================== EVENT HANDLERS ====================
  const handleStartDateChange = useCallback(
    (date: string | null | any) => {
      if (currentViewAdmin === "timelineWeek") {
        const dateAfter7Days = addDays(date, 7);
        const formattedDateEnd = format(dateAfter7Days, "yyyy-MM-dd");
        setStartDateCalendar(date);
        setEndDateCalendar(formattedDateEnd);
      } else if (currentViewAdmin === "dayGridMonth") {
        const firstDayOfMonth = startOfMonth(new Date(date));
        const formattedStartDate = format(firstDayOfMonth, "yyyy-MM-dd");
        const lastDayOfMonth = endOfMonth(new Date(date));
        const formattedEndDate = format(lastDayOfMonth, "yyyy-MM-dd");

        setStartDateCalendar(formattedStartDate);
        setEndDateCalendar(formattedEndDate);
      } else {
        setEndDateCalendar(date);
        setStartDateCalendar(date);
        setStartDateCalendarDay(date);
      }
    },
    [
      currentViewAdmin,
      setStartDateCalendar,
      setEndDateCalendar,
      setStartDateCalendarDay,
    ],
  );

  const handleFilterClick = useCallback(
    (key: any) => {
      setCalendarFilter(parseInt(key));
    },
    [setCalendarFilter],
  );

  const handleTaskFilterClick = useCallback(
    (key: string) => {
      setCalendarFilter(0);
      setTaskFilter(key);
    },
    [setTaskFilter, setCalendarFilter],
  );

  const handleGoalFilterClick = useCallback(
    (key: string) => {
      setCalendarFilter(0);
      setGoalFilter(key);
    },
    [setGoalFilter, setCalendarFilter],
  );

  const capabilityPodOptions = useMemo(() => {
    return [
      ...podDataFormatted.map((pod: any) => ({
        key: String(pod.pod_id),
        name: pod.pod_name,
      })),
    ];
  }, [podDataFormatted]);

const podStats = useMemo(() => {
  const employees = sortedSelectedEmployees;

  const totalMembers = employees.length;

  const busyMembers = employees.filter(
    (emp: any) =>
      (emp.allocated_working_hours || 0) >= (emp.total_working_hours || 0)
  );

  const idleMembersByHours = employees.filter(
    (emp: any) =>
      (emp.allocated_working_hours || 0) === 0 &&
      (emp.total_working_hours || 0) !== 0
  );

  const availableMembers = employees.filter(
    (emp: any) => emp.is_available
  );

  const totalWorkingHours = employees.reduce(
    (sum: number, emp: any) => sum + (emp.total_working_hours || 0),
    0
  );

  const totalAllocatedHours = employees.reduce(
    (sum: number, emp: any) => sum + (emp.allocated_working_hours || 0),
    0
  );

  const idleHours = Math.max(
    totalWorkingHours - totalAllocatedHours,
    0
  );

  const utilization =
    totalWorkingHours === 0
      ? 0
      : Math.round((totalAllocatedHours / totalWorkingHours) * 100);

  const idleUtilization = Math.max(100 - utilization, 0);

  return {
    totalMembers,
    busyMembers,
    availableMembers,
    idleMembersByHours,

    totalWorkingHours,
    totalAllocatedHours,
    idleHours,

    utilization,
    idleUtilization,
  };
}, [sortedSelectedEmployees]);

const conditionalRelease = (userid: any, taskPodId: any) => {
  const allowedIds = [1000003, 1000006];

  // Employee-level check (existing logic)
  const employeeMatch =
    selectedEmployee === "All"
      ? allowedIds.includes(userid)
      : selectedEmployee == userid;

  // Pod-level check: agar specific pod selected hai, to task ka pod_id match hona chahiye
  const podMatch =
    selectedPod === "0"
      ? true
      : String(taskPodId) == String(selectedPod);

  return employeeMatch && podMatch;
};

const podIdToNameMap = useMemo(() => {
  const map = new Map<string, string>();
  podDataFormatted.forEach((pod: any) => {
    map.set(String(pod.pod_id), pod.pod_name);
  });
  return map;
}, [podDataFormatted]);

// ==================== RELEASE TASKS ====================
const releaseTasksData = useMemo(() => {
  return tasksData.filter(
    (task: any) => (task.static_task_type_name === "Production Release" || task.static_task_type_name === "Merge and deploy" ) && conditionalRelease(task.assigned_to_id, task.pod_id),
  );
}, [tasksData,selectedEmployee,employeeOptions,selectedPod]);


const allEmployeesMap:any = useMemo(() => {
  const map = new Map<string, string>();
  podDataFormatted.forEach((pod: any) => {
    (pod.members || []).forEach((emp: any) => {
      map.set(String(emp.employee_id), `${emp.first_name} ${emp.last_name}`);
    });
  });
  return map;
}, [podDataFormatted]);

const releaseTasksForPopup = useMemo(() => {
  return releaseTasksData.map((task: any) => {
    return {
      taskName: task.title,
      goal_name: task.goal_name || "-",
      project_name: task.project_name || "-",
      podName: podIdToNameMap.get(String(task.pod_id)) || "-", 
      employeeName: allEmployeesMap.get(String(task.assigned_to_id)) || "-",
      date: task.start_datetime
        ? format(new Date(task.start_datetime), "MM/dd/yyyy")
        : "-",
      status: task?.current_status,
      _taskId: task.id,
    };
  });
}, [releaseTasksData, allEmployeesMap]);


  const isLoading =
    meetingsLoading || tasksLoading || meetingTypesDataLoading || goalsLoading || podLoading;


  return (
    <div
      className="w-full self-stretch relative min-h-screen overflow-hidden shrink-0 flex flex-row items-start justify-center flex-wrap content-start py-40 px-20 mq991:p-8 mq575:p-4 box-border text-left text-base text-gray font-manrope gap-[24px]"
      id="cts-respon-p-dv"
    >
      <div className="self-stretch flex flex-row items-center justify-start gap-[5px] text-2xl w-full">
        <img
          className="w-6 relative h-6 overflow-hidden shrink-0 cursor-pointer dark-white-img"
          alt="back"
          src={backArrow}
          onClick={() => navigate(-1)}
        />
        <h3 className="m-0 flex-1 relative text-inherit font-bold font-inherit inline-block max-w-[calc(100%_-_29px)] mq450:text-base dark:text-white">
          Admin Calendar
        </h3>
        <button className="cursor-pointer bg-white py-2.5 h-[44px] mq575:h-[35px] px-[42px] btn-text-purple mq575:py-[12.5px] mq575:px-[25px] rounded-radiuscomponent-button box-border flex flex-row items-center justify-center gap-[12px] border-[2px] border-solid border-surface-primary-surface hover:bg-gainsboro-100 hover:box-border hover:border-[2px] hover:border-solid hover:border-dodgerblue" onClick={()=>navigate("/calendar")}>My Calendar</button>
      </div>
      <div className="ctc-calender-main-dv main_calender_main_blg grid gap-6 mq991:contents self-stretch w-full">
        <div className="m-0 self-stretch flex flex-col items-start justify-start box-border gap-spacing-lg max-w-full mq825:pt-5 mq825:pb-5 mq825:box-border mq1425:pt-[21px] mq1425:pb-[21px] mq1425:box-border ">
          {/* Filters Row */}
          <div className="grid grid-cols-4 mq1400:grid-4 mq1275:grid-cols-3 mq950:mq1275:grid-cols-2 mq575:mq1275:grid-cols-1 items-end gap-[16px] min-w-full">
            <Controller
              name="start_date"
              control={control}
              render={({ field }) => (
                <DateSelector
                  label="Start Date*"
                  selectedDate={startDateCalendar}
                  onChange={(date) => {
                    field.onChange(date);
                    handleStartDateChange(date);
                  }}
                  nothideParsedValue={true}
                  blueIcon={true}
                />
              )}
            />
            <div className="min-w-full">
              <SearchableInputsDropdown
                label="Meeting Filter"
                placeHolder="All Meetings"
                options={meetingTypeOptions}
                onSelect={handleFilterClick}
                initialValue={selectedMeetingName}
                dontselectlastValue={true}
              />
            </div>
            <div className="min-w-full">
              <SearchableInputsDropdown
                label="Task Filter"
                placeHolder="All Tasks"
                options={statusOptions}
                onSelect={handleTaskFilterClick}
                initialValue={taskFilterInitialValue}
                dontselectlastValue={true}
              />
            </div>

            <div className="min-w-full">
              <SearchableInputsDropdown
                label="Goal Filter"
                placeHolder="All Goals"
                options={statusOptions}
                onSelect={handleGoalFilterClick}
                initialValue={goalFilterInitialValue}
                dontselectlastValue={true}
              />
            </div>
          </div>

          <div className="grid grid-cols-4 mq1400:grid-4 mq1275:grid-cols-3 mq950:mq1275:grid-cols-2 mq575:mq1275:grid-cols-1 items-end gap-[16px] min-w-full">
            <div className="min-w-full">
              <SearchableInputsDropdown
                label="Capability Pod"
                placeHolder="All Pods"
                options={capabilityPodOptions}
                onSelect={(key: any, name: any) => {
                  setSelectedPod(key);
                  setSelectedPodName(name);
                  setSelectedEmployee("All");
                  setClearEmployeeDrop(true)
                }}
                dontselectlastValue={true}
              />
            </div>
            <div className="min-w-full">
              <SearchableInputsDropdown
                label="Employee"
                placeHolder="All Employees"
                options={employeeOptions}
                onSelect={(key: any) => {
                  setSelectedEmployee(key);
                  setClearEmployeeDrop(false)
                }}
                dontselectlastValue={true}
                clearSelected={clearEmployeeDrop}
                initialValue={employeeOptions?.find((it: any) => it.key == selectedEmployee)?.name || ""}
              />
            </div>
          </div>

          {/* Legend / Info */}
          <div className="flex flex-row gap-9 mq991:gap-4  text-xs mq575:flex-col bg-white p-5 self-stretch ">

            <div
              className="flex items-center gap-2 cursor-pointer"
              onClick={() =>
                setMemberPopup({
                  open: true,
                  title: "Meetings",
                  members: meetingsForPopup,
                  columns: [
                    { key: "title", label: "Title" },
                    { key: "dateTime", label: "Date/Time" },
                    { key: "meeting_type_name", label: "Type" },
                    { key: "host_name", label: "Host Name" },
                    { key: "attendees", label: "Attendees" },
                    { key: "_view", label: "View" },
                  ],
                  onViewItem: (item) =>
                    navigate("/meeting_detail", {
                      state: {
                        meetingID: item._meetingId,
                        startMeetDate: item._startMeetDate,
                        endMeetDate: item._endMeetDate,
                      },
                    }),
                })
              }
            >
              <div className="w-4 h-4 rounded bg-blue-500" />
              <span className="text-gray-600 dark:text-gray-400">Meetings</span>
              <span className="emplink-item-dv text-sm font-semibold dark:text-white">
                ({meetingData.length})
              </span>
            </div>
            
            {/* <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-release-item" />
              <span className="text-gray-600 dark:text-gray-400">Release</span>
            </div> */}
            <div
  className="flex items-center gap-2 cursor-pointer"
  onClick={() =>
    setMemberPopup({
      open: true,
      title: "Production Release Tasks",
      members: releaseTasksForPopup,
      columns: [
            { key: "project_name", label: "Project" },
              { key: "podName", label: "Pod" }, 
             { key: "goal_name", label: "Goal" },
        { key: "taskName", label: "Task" },
       
    
        { key: "employeeName", label: "Employee" },
        { key: "date", label: "Date" },
         { key: "status", label: "Status" },
        { key: "_view", label: "View" },
      ],
      onViewItem: (item) =>
        navigate("/task_detail", { state: { taskId: item._taskId } }),
    })
  }
>
  <div className="w-4 h-4 rounded bg-release-item" />
  <span className="text-gray-600 dark:text-gray-400">Release</span>
  <span className="emplink-item-dv text-sm font-semibold dark:text-white">
    ({releaseTasksData.length})
  </span>
</div>

            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-aliceblue" />
              <span className="text-gray-600 dark:text-gray-400">
                Not Started
              </span>
            </div>

            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-antiquewhite" />
              <span className="text-gray-600 dark:text-gray-400">
                In Progress
              </span>
            </div>

            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-antiquewhite-testing " />
              <span className="text-gray-600 dark:text-gray-400">Testing</span>
            </div>

            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-honeydew" />
              <span className="text-gray-600 dark:text-gray-400">Closed</span>
            </div>
          </div>

          {/* Members Info */}
          <div className="grid  flex-row gap-1 mq991:gap-4  text-xs mq575:flex-col bg-white p-0 self-stretch .bg-surface-bg-blue p-5">


            <label className="self-stretch relative font-semibold pb-2 dark:text-white">{selectedPodName} POD Summary</label>
            <div className="flex gap-2">
              <div className="flex gap-2">
                <span className="text-sm dark:text-white">
                  Total Hours:
                </span>
                <span className="text-sm font-semibold dark:text-white">
                  {podStats?.totalWorkingHours.toFixed(2)} hrs
                </span>
              </div>

              <div className="flex gap-2">
                <span className="text-sm dark:text-white">
                  Total Allocated Hours:
                </span>
                <span className="text-sm font-semibold dark:text-white">
                  {podStats?.totalAllocatedHours.toFixed(2)} hrs
                </span>
              </div>
            <div className="flex gap-2">
                <span className="text-sm dark:text-white">
                  Total Idle Hours:
                </span>
                <span className="text-sm font-semibold dark:text-white">
                  {podStats?.idleHours.toFixed(2)} hrs
                </span>
              </div>
            </div>
            <div className="flex gap-10 bg-white ">
              <div className="flex gap-2">
                <span className="text-sm dark:text-white"> Total Members :</span>
                <span className="emplink-item-dv text-sm dark:text-white"
                  onClick={() =>
                    setMemberPopup({
                      open: true,
                      title: "Total Members",
                      members: sortedSelectedEmployees,
                      columns: [
                        {
                          key: "employeeName",
                          label: "Employee",
                        },
                            {
                          key: "employee_role",
                          label: "Role",
                        },
                      ],
                    })
                  }
                >
                  {podStats.totalMembers}
                </span>
              </div>

              <div className="flex gap-2">

                <span className="text-sm dark:text-white">  Busy Members :</span>
                <span className="emplink-item-dv text-sm dark:text-white"
                  onClick={() =>
                    setMemberPopup({
                      open: true,
                      title: "Busy Members",
                      members: podStats.busyMembers,
                      columns: [
                        {
                          key: "employeeName",
                          label: "Employee",
                        },
                        {
                          key: "total_working_hours",
                          label: "Total Hours",
                        },
                        {
                          key: "allocated_working_hours",
                          label: "Allocated Hours",
                        },
                        {
                          key: "available_hours",
                          label: "Available Hours",
                        },
                        {
                          key: "utilization_percentage",
                          label: "Utilization %",
                        },
                      ],
                    })
                  }
                >
                  {podStats.busyMembers.length}
                </span>
              </div>

              <div className="flex gap-2">

                <span className="text-sm dark:text-white">  Available Members :</span>
                <span className="emplink-item-dv text-sm dark:text-white"
                  onClick={() =>
                    setMemberPopup({
                      open: true,
                      title: "Available Members",
                      members: podStats.availableMembers,
                      columns: [
                        {
                          key: "employeeName",
                          label: "Employee",
                        },
                        {
                          key: "total_working_hours",
                          label: "Total Hours",
                        },
                        {
                          key: "allocated_working_hours",
                          label: "Allocated Hours",
                        },
                        {
                          key: "available_hours",
                          label: "Available Hours",
                        },
                        {
                          key: "utilization_percentage",
                          label: "Utilization %",
                        },
                      ],
                    })
                  }
                >
                  {podStats.availableMembers.length}
                </span>
              </div>

              <div className="flex gap-2">

                <span className="text-sm dark:text-white">  Idle Members :</span>
                <span className="emplink-item-dv text-sm dark:text-white"
                  onClick={() =>
                    setMemberPopup({
                      open: true,
                      title: "Idle Members",
                      members: podStats.idleMembersByHours,
                      columns: [
                        {
                          key: "employeeName",
                          label: "Employee",
                        },
                        {
                          key: "total_working_hours",
                          label: "Total Hours",
                        },
                        {
                          key: "allocated_working_hours",
                          label: "Allocated Hours",
                        },
                        {
                          key: "available_hours",
                          label: "Available Hours",
                        },
                        {
                          key: "utilization_percentage",
                          label: "Utilization %",
                        },
                      ],
                    })
                  }
                >
                  {podStats.idleMembersByHours.length}
                </span>
              </div>

              <div className="flex gap-2">

                <span className="text-sm dark:text-white">   Utilization :</span>
                <span className="emplink-item-dv text-sm dark:text-white"
                  onClick={() =>
                    setMemberPopup({
                      open: true,
                      title: "Utilization",
                      members: sortedSelectedEmployees,
                      columns: [
                        {
                          key: "employeeName",
                          label: "Employee",
                        },
                        {
                          key: "total_working_hours",
                          label: "Total Hours",
                        },
                        {
                          key: "allocated_working_hours",
                          label: "Allocated Hours",
                        },
                        {
                          key: "available_hours",
                          label: "Available Hours",
                        },
                        {
                          key: "utilization_percentage",
                          label: "Utilization %",
                        },
                      ],
                    })
                  }
                >
                  {podStats.utilization}%
                </span>
              </div>

                <div className="flex gap-2">

                <span className="text-sm dark:text-white">Idle  Utilization :</span>
                <span className=" text-sm dark:text-white"
                >
                  {podStats.idleUtilization}%
                </span>
              </div>
            </div>
          </div>

        </div>
        {/* Calendar Section */}
        <div className="self-stretch flex flex-col items-start justify-start [row-gap:20px] mq950:flex-wrap wizkind_calendar_main_dv calendar-leftbar-dv">
          {startDateCalendar  && (
            <div className="shadow-[2px_2px_32px_rgba(0,_19,_85,_0.08)] rounded-lg bg-white box-border overflow-hidden shrink-0 flex items-end justify-start min-w-full mq825:min-w-full max-w-full text-xs text-text-dark-shade border-[1px] border-stroke mq1425:flex-1 caldender-right-main-blg">
              <CalendarAdmin
                setCalendarDate={setCalendarDate}
                setCurrentViewAdmin={setCurrentViewAdmin}
                startDate={startDateCalendar}
                calendarDate={calendarDate}
                setStartDateCalendar={setStartDateCalendar}
                setEndDateCalendar={setEndDateCalendar}
                setStartDateCalendarDay={setStartDateCalendarDay}
                currentViewAdmin={currentViewAdmin}
                resources={resources}
                events={resourceEvents}
              />
            </div>
          )}
        </div>
      </div>
      {isLoading && <Loader />}
      <div className="pdo-modelpup-ui-item">
        <MemberListPopup
          open={memberPopup.open}
          title={memberPopup.title}
          members={memberPopup.members}
          columns={memberPopup.columns}
          onViewItem={memberPopup.onViewItem}
          onClose={() =>
            setMemberPopup({
              open: false,
              title: "",
              members: [],
              columns: [],
            })
          }
        />
      </div>
    </div>
  );
};

export default ViewCalendarAdmin;